import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import gameData from '../gameData';
import DraggableSprite from '../skeletal/DraggableSprite';
import SpriteLabel from '../skeletal/SpriteLabel';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import LabelGameScreen from './LabelGameScreen';
import RoundOverPopup from '../skeletal/RoundOverPopup';
import LocalizationSoundAssetKeys from '../Localization-SoundAssetKeys';

export default class AssemblyGameScreen extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  init() {
    this.attempts = 0;

    this.boneMaskNames = ['left_hand_m',
      'right_hand_m',
      'left_fibula_m',
      'left_foot_m',
      'left_humerus_m',
      'left_radius_m',
      'left_femur_m',
      'left_tibia_m',
      'left_ulna_m',
      'pelvis_m',
      'right_fibula_m',
      'right_foot_m',
      'right_humerus_m',
      'right_radius_m',
      'right_femur_m',
      'right_tibia_m',
      'right_ulna_m',
      'scapula_m',
      'skull_m',
      'spine_m',
      'clavicle_m',
      'ribs_m',
      'sternum_m',
      'face_m',
      'right_patella_m',
      'left_patella_m'];

    this.boneNames = ['left_hand',
      'right_hand',
      'left_fibula',
      'left_foot',
      'left_humerus',
      'left_radius',
      'left_femur',
      'left_tibia',
      'left_ulna',
      'pelvis',
      'right_fibula',
      'right_foot',
      'right_humerus',
      'right_radius',
      'right_femur',
      'right_tibia',
      'right_ulna',
      'scapula',
      'skull',
      'spine',
      'clavicle',
      'ribs',
      'sternum',
      'face',
      'right_patella',
      'left_patella'];

    this.bonesPlacedCorrectly = 0;

    /** @type {DraggableSprite[]} */
    this.boneSprites = [];

    /** @type {Phaser.GameObjects.Image[]} */
    this.boneTargets = [];

    /** @type {{x:number, y:number}[]} */
    this.bonePositions = [];

    this.adjustY = 100;

    // drag fix
    this.input.on(Phaser.Input.Events.GAME_OUT, (time, event) => {
      if (event instanceof MouseEvent) {
        this.input.manager.onMouseUp(event);
      }
    });
  }

  create() {
    const width = this.scale.width;
    const height = this.scale.height;

    // bg
    const bgShape = this.add.rectangle(0, 0, width, height, 0x1793C7).setOrigin(0);

    const manOutline = ABCyaPhaser.Images.addImage(width / 2, 288 + this.adjustY, UIAssetKeys._man_outline, this).setScale(0.7);

    for (let i = 0; i < this.boneMaskNames.length; i++) {
      const name = this.boneMaskNames[i];

      const bmap = ABCyaPhaser.Images.addImage(0, 0, name, this).setScale(0.7);
      bmap.name = name;
      this.boneTargets.push(bmap);
    }

    this.positionBoneMasks();

    this.makePositions();

    for (let i = 0; i < this.boneNames.length; i++) {
      const spriteName = this.boneNames[i];
      this.placeBone(spriteName, i);
    }

    // add hit target for left_patella to right patella draggable object
    // and vice versa
    let leftPatellaSprite = null;
    let rightPatellaSprite = null;

    for (let i = 0; i < this.boneNames.length; i++) {
      const spriteName = this.boneNames[i];
      const sprite = this.boneSprites[i];

      if (spriteName === 'left_patella') {
        leftPatellaSprite = sprite;
      }
      else if (spriteName === 'right_patella') {
        rightPatellaSprite = sprite;
      }
    }

    const rightPatellaHitTarget = rightPatellaSprite.hitTargets[0];
    const leftPatellaHitTarget = leftPatellaSprite.hitTargets[0];

    rightPatellaSprite.hitTargets.push(leftPatellaHitTarget);
    leftPatellaSprite.hitTargets.push(rightPatellaHitTarget);

    this.soundID = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    ABCyaPhaser.Audio.playSound(LocalizationSoundAssetKeys._assemble_the_skeleton, this.soundID);

    this.makeClicksLabel();
  }

  positionBoneMasks() {
    for (let i = 0; i < this.boneTargets.length; i++) {
      const sprite = this.boneTargets[i];

      const spriteName = `${sprite.name}`;
      switch (spriteName) {
        case 'clavicle_m':
          sprite.x = 513;
          sprite.y = 106;
          break;

        case 'skull_m':
          sprite.x = 512;
          sprite.y = 40;
          break;

        case 'face_m':
          sprite.x = 512;
          sprite.y = 60;
          break;

        case 'pelvis_m':
          sprite.x = 513;
          sprite.y = 263;
          break;

        case 'spine_m':
          sprite.x = 513;
          sprite.y = 173;
          break;

        case 'right_femur_m':
          sprite.x = 481;
          sprite.y = 340;
          break;

        case 'left_femur_m':
          sprite.x = 546;
          sprite.y = 338;
          break;

        case 'left_foot_m':
          sprite.x = 544;
          sprite.y = 544;
          break;

        case 'right_foot_m':
          sprite.x = 480;
          sprite.y = 544;
          break;

        case 'left_tibia_m':
          sprite.x = 537;
          sprite.y = 470;
          break;

        case 'right_tibia_m':
          sprite.x = 488;
          sprite.y = 470;
          break;

        case 'left_fibula_m':
          sprite.x = 544;
          sprite.y = 468;
          break;

        case 'right_fibula_m':
          sprite.x = 481;
          sprite.y = 469;
          break;

        case 'left_patella_m':
          sprite.x = 541;
          sprite.y = 410;
          break;

        case 'right_patella_m':
          sprite.x = 487;
          sprite.y = 411;
          break;

        case 'scapula_m':
          sprite.x = 512;
          sprite.y = 131;
          break;

        case 'left_humerus_m':
          sprite.x = 575;
          sprite.y = 159;
          break;

        case 'right_humerus_m':
          sprite.x = 452;
          sprite.y = 159;
          break;

        case 'right_radius_m':
          sprite.x = 432;
          sprite.y = 240;
          break;

        case 'left_radius_m':
          sprite.x = 594;
          sprite.y = 241;
          break;

        case 'ribs_m':
          sprite.x = 513;
          sprite.y = 145;
          break;

        case 'left_hand_m':
          sprite.x = 611;
          sprite.y = 304;
          break;

        case 'right_hand_m':
          sprite.x = 413;
          sprite.y = 302;
          break;

        case 'left_ulna_m':
          sprite.x = 583;
          sprite.y = 242;
          break;

        case 'right_ulna_m':
          sprite.x = 443;
          sprite.y = 242;
          break;

        case 'sternum_m':
          sprite.x = 513;
          sprite.y = 123;
          break;
      }

      sprite.y += this.adjustY;
    }
  }

  // make 26 positions for bones and randomize them
  // 13 on each side of man outline
  makePositions() {
    for (let i = 0; i < this.boneNames.length; i++) {
      let x = 0;
      let y = 0;

      if (i < 13) {
        x = ((i % 4) * 90) + 70;
        if (i < 4) {
          y = 85;
        }
        else if (i < 8) {
          y = 235;
        }
        else if (i < 12) {
          y = 385;
        }
        else {
          y = 535;
        }
      }
      else if (i >= 13) {
        x = ((i % 4) * 90) + 670;
        if (i < (4 + 13)) {
          y = 85;
        }
        else if (i < (8 + 13)) {
          y = 235;
        }
        else if (i < (12 + 13)) {
          y = 385;
        }
        else {
          y = 535;
        }
      }

      y += this.adjustY;

      this.bonePositions.push({ x, y });
    }

    this.bonePositions = Phaser.Utils.Array.Shuffle(this.bonePositions);
  }

  placeBone(spriteName, i) {
    const target = this.boneTargets[i];

    const sprite = new DraggableSprite(this, spriteName);
    sprite.game = 'assemble';
    sprite.adjustY = 30;

    sprite.enabled = true;
    this.boneSprites.push(sprite);

    sprite.scale = 0.70;

    sprite.hitTargets.push(target);

    sprite.hitListener = this.targetHit.bind(this);
    sprite.outListener = this.targetOut.bind(this);
    sprite.doClickAction = this.dragMouseDown.bind(this);

    this.add.existing(sprite);

    const posArray = this.bonePositions[i];

    sprite.x = posArray.x;
    sprite.y = posArray.y;

    sprite.lastPosX = sprite.x;
    sprite.lastPosY = sprite.y;
  }

  targetHit(sprite) {
    DraggableSprite.showCorrect();

    this.attempts++;

    this.clicksLabel.setValue(this.attempts);

    sprite.setPosition(sprite.hitTargets[0].x, sprite.hitTargets[0].y);

    // eslint-disable-next-line no-param-reassign
    sprite.enabled = false;

    this.reorderBoneSprites();

    this.bonesPlacedCorrectly++;
    if (this.bonesPlacedCorrectly === this.boneNames.length) {
      this.gameWon();
    }
  }

  reorderBoneSprites() {
    for (let i = 0; i < this.boneSprites.length; i++) {
      const sprite = this.boneSprites[i];

      if (sprite.parentContainer) {
        sprite.parentContainer.bringToTop(sprite);
      }
      else {
        this.children.bringToTop(sprite);
      }
    }
  }

  gameWon() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cartoon_win, this);

    // best score
    let bestScore = this.attempts;
    if (bestScore > gameData.ASSEMBLY_BEST_SCORE) {
      bestScore = gameData.ASSEMBLY_BEST_SCORE;
    }
    else {
      gameData.ASSEMBLY_BEST_SCORE = bestScore;
    }

    this.wonDialog = new RoundOverPopup(this, 2, this.attempts, bestScore);
    this.wonDialog.x = this.scale.width / 2;
    this.wonDialog.y = this.scale.height / 2;
    this.add.existing(this.wonDialog);

    this.wonDialog.doAction1 = this.playAgain.bind(this);
    this.wonDialog.doAction2 = this.playOtherGame.bind(this);

    ABCyaPhaser.Tweens.addTween(this.wonDialog, 900, { scale: 1.2 }, Phaser.Math.Easing.Bounce.Out);
  }

  playAgain() {
    this.playAssembleGame();
  }

  playOtherGame() {
    this.playLabelGame();
  }

  playAssembleGame() {
    // restart the assemble game
    this.scene.start();
  }

  playLabelGame() {
    this.scene.start(LabelGameScreen.KEY);
  }

  /**
   * 
   * @param {DraggableSprite} sprite 
   */
  targetOut(sprite) {
    DraggableSprite.showIncorrect();

    this.attempts++;
    this.clicksLabel.setValue(this.attempts);

    sprite.moveToLastPositionAndRotate();
  }

  dragMouseDown() {
    if (this.soundID) {
      this.soundID.destroy();
      this.soundID = null;
    }
  }

  makeClicksLabel() {
    this.clicksLabel = new SpriteLabel(this, 2);
    this.clicksLabel.x = this.scale.width - 105;
    this.clicksLabel.y = this.scale.height - 50;

    this.add.existing(this.clicksLabel);
  }
}